import React, { memo } from 'react';
import { Lightbulb } from 'lucide-react';
import styled, { css } from 'styled-components';

/**
 * Тон вывода: `good` — хорошая новость, `bad` — плохая, `warn` — стоит
 * посмотреть, `neutral` — «данных мало» / без оценки, `info` — просто факт.
 */
export type InsightTone = 'good' | 'bad' | 'warn' | 'neutral' | 'info';

export interface IInsightLineProps {
  /** Готовая фраза — одна, из тех же чисел, что на экране. */
  text: string;
  tone?: InsightTone;
}

const TONES: Readonly<
  Record<InsightTone, { bar: string; bg: string; icon: string }>
> = {
  good: {
    bar: 'var(--k-fg-16a34a)',
    bg: 'var(--k-bg-f0fdf4)',
    icon: 'var(--k-fg-15803d)',
  },
  bad: {
    bar: 'var(--k-fg-dc2626)',
    bg: 'var(--k-bg-fef2f2)',
    icon: 'var(--k-fg-b91c1c)',
  },
  warn: {
    bar: 'var(--k-fg-d97706)',
    bg: 'var(--k-bg-fffbeb)',
    icon: 'var(--k-fg-b45309)',
  },
  neutral: {
    bar: 'var(--k-fg-94a3b8)',
    bg: 'var(--k-bg-f8fafc)',
    icon: 'var(--k-fg-64748b)',
  },
  info: {
    bar: 'var(--k-fg-2563eb)',
    bg: 'var(--k-bg-eff6ff)',
    icon: 'var(--k-fg-1d4ed8)',
  },
};

/**
 * ТЗ 0103, повторное ревью (пакет 2) — вывод экрана одной фразой: «что
 * происходит» над графиком, без графики и кнопок. Текст собирает модель из
 * тех же чисел, что на экране; молекула только рисует его в тоне. Подпись
 * приходит пропсом — внутри нет ключей перевода.
 */
export const InsightLine = memo(
  ({ text, tone = 'info' }: IInsightLineProps) => (
    <Line role="note" $tone={tone}>
      <Lightbulb size={14} strokeWidth={2} aria-hidden="true" />
      <span>{text}</span>
    </Line>
  ),
);

InsightLine.displayName = 'InsightLine';

const Line = styled.p<{ $tone: InsightTone }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.45;
  color: var(--k-fg-0f172a);
  overflow-wrap: anywhere;

  ${({ $tone }) => css`
    border-left: 3px solid ${TONES[$tone].bar};
    background: ${TONES[$tone].bg};

    & > svg {
      flex: none;
      margin-top: 3px;
      color: ${TONES[$tone].icon};
    }
  `}
`;
